import React, { FC, memo, useMemo, useCallback } from "react";


// Constants
import {
    ADD_ICON_ASSETS,
    ADD_ICON_DIMENSIONS,
    CONSTANTS,
    PLAN_ICON_DIMENSIONS,
    SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS,
} from "../../constants";
import { MAX_POLLING_COUNT, COMPARISON_HALFCARD_STORAGE_KEY } from "@super-ui/components/cart-upsell-nudge/constants";

// Styled Components
import {
    StyledPlanCard,
    PlanContent,
    PlanInfo,
    PlanIcon,
    BasePrice,
    PriceContainer,
    AddCta,
    RemoveCta,
    PlanText,
    RemoveSectionContainer,
    AddCtaImage,
} from "./styles";

// Components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// Types
import { BUTTON_VARIANT_TYPE, SubscriptionRenewalAnalytics, SubscriptionRenewalHandlers } from "../../types";
import { ComparisonNudgeItem } from "@swiggy-private/cart-types";
import { ProgramSubTypes } from "@super-ui/interfaces/SwiggyOne";

// Helpers
import { priceParser, attachQueryParams } from "@super-ui/components/cart-upsell-nudge/helpers";
import { isEmpty, replaceText } from "@swiggy-private/js-utils";

// hooks
import { useEffectOnce } from "@swiggy-private/core-ui";
import { usePolling } from "@super-ui/components/cart-upsell-nudge/hooks/usePolling";

export interface PlanCardProps {
    planData: ComparisonNudgeItem;
    index: number;
    handlers: SubscriptionRenewalHandlers;
    analyticsEvents?: SubscriptionRenewalAnalytics;
    setShowRemovePlanConfirmation?: (value: boolean) => void;
}

const PlanCard: FC<PlanCardProps> = ({ planData, handlers, analyticsEvents, setShowRemovePlanConfirmation }) => {
    const planId = planData.plan_details?.plan_id;
    const planType = planData.plan_details?.type;
    const isBlackPlan = useMemo(() => {
        return planType?.toUpperCase() === ProgramSubTypes.BLACK;
    }, [planType]);

    const isButtonVariantAdd = useMemo(() => {
        return planData?.ctas?.[1]?.type === BUTTON_VARIANT_TYPE.ADD_PLAN;
    }, [planData?.ctas]);

    // Setup polling for plan comparison halfcard
    const planInCart = useMemo(() => {
        if (planData.ctas?.[1]?.type === BUTTON_VARIANT_TYPE.REMOVE_PLAN) {
            return String(planId);
        }
        return "";
    }, [planData.ctas, planId]);

    const { addSuperToCart, removeSuperFromCart, navigateToDeeplinkGenericHelper } = handlers;

    const payload = useMemo(
        () => ({
            addSuperToCart,
            maxPollingCount: MAX_POLLING_COUNT,
            appStorageKey: COMPARISON_HALFCARD_STORAGE_KEY,
            planInCart,
        }),
        [addSuperToCart, planInCart],
    );

    const { startPollingAction } = usePolling(payload);

    useEffectOnce(() => {
        if (isButtonVariantAdd) {
            analyticsEvents?.onImpressionSubscriptionRenewalPlanNudge(planData);
        } else {
            analyticsEvents?.onImpressionSubscriptionRenewalRemovalCta(planData);
        }
    });

    const handleAddPlanClick = useCallback(() => {
        if (planId) {
            analyticsEvents?.onClickAddPlanCta(planData);
            addSuperToCart(String(planId), planType);
        }
    }, [planId, planType, addSuperToCart, analyticsEvents, planData]);

    const handleRemovePlanClick = useCallback(() => {
        analyticsEvents?.onClickRemovePlanCta(planData);
        if (setShowRemovePlanConfirmation) {
            setShowRemovePlanConfirmation(true);
        } else {
            removeSuperFromCart();
        }
    }, [removeSuperFromCart, planData, analyticsEvents, setShowRemovePlanConfirmation]);

    // Parse price information from the new structure
    const { basePrice, newPrice } = useMemo(() => {
        return priceParser(planData?.note || "");
    }, [planData.note]);

    // Handle plan info CTA click (similar to ChangePlanCta with polling)
    const handlePlanInfoCTA = useCallback(() => {
        if (!isEmpty(planData.ctas?.[0]?.link) && navigateToDeeplinkGenericHelper) {
            analyticsEvents?.onClickComparePlanCta(planData);

            startPollingAction();

            const formattedLink: string = attachQueryParams(planData.ctas?.[0]?.link || "", {
                planId,
                planInCart,
                appStorageKey: COMPARISON_HALFCARD_STORAGE_KEY,
            });
            navigateToDeeplinkGenericHelper(formattedLink);
        }
    }, [navigateToDeeplinkGenericHelper, analyticsEvents, planData, planId, planInCart, startPollingAction]);

    const logoSrc = useMemo(() => {
        return planData?.logo_ctx?.logo;
    }, [planData?.logo_ctx]);

    const planText = useMemo(() => {
        return replaceText(planData?.ctas?.[0]?.text || "", "", true);
    }, [planData?.ctas]);

    const addIconCloudinaryId = useMemo(() => {
        return isBlackPlan ? ADD_ICON_ASSETS.ONE_BLACK : ADD_ICON_ASSETS.ONE;
    }, [isBlackPlan]);

    return (
        <StyledPlanCard
            data-testid={SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS.PLANS_CONTAINER}
            isRemoveCta={!isButtonVariantAdd}
            isBlackPlan={isBlackPlan}
            aria-label={`${planData.plan_details.name} - ${planData.sub_titles?.[0] || ""}`}
        >
            <PlanContent>
                <PlanInfo isRemoveCta={!isButtonVariantAdd}>
                    <PlanIcon
                        cloudinaryId={logoSrc}
                        alt={planData.logo_ctx?.text}
                        cloudinaryHeight={PLAN_ICON_DIMENSIONS.HEIGHT * 2}
                        cloudinaryWidth={PLAN_ICON_DIMENSIONS.WIDTH * 2}
                        height={`${PLAN_ICON_DIMENSIONS.HEIGHT}px`}
                        width={`${PLAN_ICON_DIMENSIONS.WIDTH}px`}
                    />

                    <PlanText
                        onClick={handlePlanInfoCTA}
                        data-testid={SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS.PLAN}
                        variant="V2.body3.neutral.bold"
                        color={isBlackPlan ? "secondary_One_Blck" : "primary"}
                        dangerouslySetInnerHTML={{ __html: planText || "" }}
                    />
                </PlanInfo>
                {planData?.sub_titles?.[0] && (
                    <Text variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                        {planData?.sub_titles?.[0]}
                    </Text>
                )}
            </PlanContent>

            {isButtonVariantAdd ? (
                <AddCta
                    onClick={handleAddPlanClick}
                    aria-label="Add plan to cart"
                    as="button"
                    type="button"
                    data-testid={SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS.PLAN_ADD_BUTTON}
                >
                    <AddCtaImage
                        cloudinaryId={addIconCloudinaryId}
                        width={`${ADD_ICON_DIMENSIONS.WIDTH}px`}
                        height={`${ADD_ICON_DIMENSIONS.HEIGHT}px`}
                        cloudinaryHeight={ADD_ICON_DIMENSIONS.HEIGHT * 2}
                        cloudinaryWidth={ADD_ICON_DIMENSIONS.WIDTH * 2}
                        alt={CONSTANTS.ADD_ICON_ALT}
                    />
                </AddCta>
            ) : (
                <RemoveSectionContainer>
                    {planData.ctas?.[1]?.text && (
                        <RemoveCta
                            onClick={handleRemovePlanClick}
                            aria-label={`Remove ${planData.ctas[1].text}`}
                            as="button"
                            type="button"
                            data-testid={SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS.PLAN_REMOVE_BUTTON}
                        >
                            <Text variant="V2.overline.neutral.medium" color="text_Med_Emphasis">
                                {planData.ctas[1].text}
                            </Text>
                        </RemoveCta>
                    )}

                    {(newPrice || basePrice) && (
                        <PriceContainer data-testid={SUBSCRIPTION_RENEWAL_NUDGE_TEST_IDS.PLAN_PRICE}>
                            {basePrice && (
                                <BasePrice
                                    variant="V2.caption.neutral.medium"
                                    color="text_Med_Emphasis"
                                    dangerouslySetInnerHTML={{ __html: basePrice }}
                                />
                            )}
                            {newPrice && (
                                <Text
                                    variant="V2.body2.neutral.medium"
                                    color="text_High_Emphasis"
                                    dangerouslySetInnerHTML={{ __html: newPrice }}
                                />
                            )}
                        </PriceContainer>
                    )}
                </RemoveSectionContainer>
            )}
        </StyledPlanCard>
    );
};

export default memo(PlanCard);
